import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { ListPagination } from '@/components/ListPagination';
import { useServerPagination } from '@/hooks/useServerPagination';
import { Search } from 'lucide-react';
import { useDebounce } from '@/hooks/useDebounce';
import { driverProfileName } from '@/lib/driverProfile';

export default function LoanInstalmentsRecords() {
    const [searchTerm, setSearchTerm] = useState('');
    const debouncedSearch = useDebounce(searchTerm, 300);

    const { data: countData } = useQuery({
        queryKey: ['loan-instalments-count'],
        queryFn: async () => {
            const { count, error } = await supabase.from('loan_instalments').select('*', { count: 'exact', head: true });
            if (error) throw error;
            return count || 0;
        },
    });

    const { currentPage, totalPages, totalItems, from, to, goToPage, resetPage } = useServerPagination({ totalCount: countData || 0 });

    useEffect(() => { resetPage(); }, [debouncedSearch]);

    const { data: instalments, isLoading } = useQuery({
        queryKey: ['loan-instalments', currentPage],
        queryFn: async () => {
            const { data, error } = await supabase
                .from('loan_instalments')
                // `admin_id` IS SELECTED SO THE DRIVER'S OWN ROW CAN BE PICKED OUT — see
                // `driverProfileOf`; the embed can carry an admin's test profile beside it.
                .select('*, loans(contracts(drivers(profiles(id, admin_id, first_name, last_name))))')
                .order('starts_at', { ascending: false })
                .range(from, to);
            if (error) throw error;
            return data;
        },
        enabled: countData !== undefined,
    });

    const formatCurrency = (amount: number) => new Intl.NumberFormat('en-GH', { style: 'currency', currency: 'GHS' }).format(amount);

    return (
        <Card className="border-0">
            <CardHeader>
                <div className="relative w-full sm:w-64">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input placeholder="Search..." className="pl-9" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                </div>
            </CardHeader>
            <CardContent>
                <ListPagination currentPage={currentPage} totalPages={totalPages} onPageChange={goToPage} totalItems={totalItems} />
                {isLoading ? (
                    <Skeleton className="h-48 w-full" />
                ) : instalments && instalments.length > 0 ? (
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Starts</TableHead>
                                <TableHead>Advance</TableHead>
                                <TableHead>Driver</TableHead>
                                <TableHead className="text-right">Amount</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {instalments.map((i) => {
                                const driver = driverProfileName(i.loans?.contracts?.drivers?.profiles) ?? '-';
                                return (
                                    <TableRow key={i.id}>
                                        <TableCell>{i.starts_at ? new Date(i.starts_at).toLocaleDateString(undefined, { timeZone: 'UTC' }) : '-'}</TableCell>
                                        <TableCell>#{i.loan_id}</TableCell>
                                        <TableCell>{driver}</TableCell>
                                        <TableCell className="text-right font-medium">{formatCurrency(i.amount)}</TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                ) : (
                    <p className="text-center text-muted-foreground py-8">No advance instalments found</p>
                )}
            </CardContent>
        </Card>
    );
}
